import { Chart as ChartJS, Tooltip, Legend, ArcElement } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import styled from "styled-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "context/WindowSizeContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const ChartBox = styled.div`
    width: 100%;
    //height: 250px;
    min-height: 250px;
    height: auto;
`;
const ChartImageWrap = styled.div`
    display: flex;
    justify-content: ${({ $hasImage }) => ($hasImage ? "space-between" : "center")};
    width: 100%;
    transition: justify-content 0.3s ease-in-out, gap 0.3s ease-in-out;
`;
const ChartContainer = styled.div`
    width: ${(props) => ((props.$hasImage && !props.$isMobile) ? "60%" : (props.$hasImage && props.$isMobile) ? "65%" : "100%")};
    height: ${(props) => ((props.$hasImage && !props.$isMobile) ? "220px" : (props.$hasImage && props.$isMobile) ? "195px" : "250px")};
    transition: width 0.3s ease-in-out, margin-left 0.3s ease-in-out;
    margin-bottom: ${(props) => props.$isMobile && "3px"};
`;
const LegendBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    height: auto;
`;
const LegendItem = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: default;
    user-select: none;
    width: ${(props) => props.$isMobile ? "73px" : "75px"};
    height: ${(props) => props.$isMobile ? "24px" : "28px"};
    padding: 2px;
    border-radius: 5px;
    background-color: ${(props) => (props.$isMobile && props.$isActive) || (!props.$isMobile && props.$isActive) ? "rgba(0, 0, 0, 0.1)" : "transparent"};
    transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
    &:hover {
        background-color: ${(props) => !props.$isMobile && "rgba(0, 0, 0, 0.1)"};
    }
    span {
        display: inline-block;
        width: 6.5px;
        height: 6.4px;
        margin-right: 6px;
        background-color: ${(props) => props.color};
    }
    p {
        color: #666;
        font-family: 'Arial';
        font-size: 12px;
    }
`;
const TooltipBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    width: ${(props) => props.$singleImage ? "80px" : "160px"};
    height: 80px;
    background: white;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    font-size: 12px;
    white-space: nowrap;
    opacity: ${(props) => props.$imageVisible ? 1 : 0};
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
    bottom: 35px;
    gap: 10px;

    &::before {
        content: "";
        position: absolute;
        bottom: ${(props) => props.$isMobile ? "-11px" : "-9px"};
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        border-style: solid;
        border-color: #ccc transparent;
        border-width: ${(props) => props.$isMobile ? "11px 11px 0 11px" : "9px 9px 0 9px"};
        z-index: 0;
    }
    &::after {
        content: "";
        position: absolute;
        bottom: ${(props) => props.$isMobile ? "-10px" : "-8px"};
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        border-style: solid;
        border-color: #fff transparent;
        border-width: ${(props) => props.$isMobile ? "10px 10px 0 10px" : "8px 8px 0 8px"};
        z-index: 1;
    }
`;
const StyledImage = styled.img`
    width: 60px;
    height: 60px;
    object-fit: contain;
    margin-bottom: 5px;
    border-radius: 5px;
    opacity: ${(props) => (props.$imageVisible ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
`;
const EqmtByTypeChart = React.memo(({ salesData, text, unit, label }) => {

    const isMobile = useWindowSize();
    const { t } = useTranslation();
    const everyData = salesData.every(data => data.amount === 0);
    const unitTitle = unit === undefined ? '' : unit;
    const title = text === undefined ? '' : text;
    const [selectedIndex, setSelectedIndex] = useState(null); // 클릭된 범례
    const [tooltipData, setTooltipData] = useState({ image: null, label: "" });
    const [imageVisible, setImageVisible] = useState(false); // 이미지 보기/숨기기
    const [selectedImage, setSelectedImage] = useState(null);
    const legendRef = useRef(null);

    const isImageData = salesData.some((item) => item.image);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (legendRef.current && !legendRef.current.contains(event.target)) {
                setSelectedIndex(null);
                setSelectedImage((prev) => { return prev });
                setImageVisible(false);
            }
        };

        if (isMobile) {
            document.addEventListener("click", handleOutsideClick);
        }

        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, [isMobile]);

    /* web */
    // 마우스 호버 시 이벤트트
    const handleLegendHover = (index, event) => {
        if (!isImageData || !salesData || index < 0 || index >= salesData.length) return;

        const hoveredItem = salesData?.[index];
        if (!hoveredItem) return;

        setSelectedIndex(index);
        setTooltipData({ image: hoveredItem.image, label: hoveredItem.label });
        setImageVisible(true);
    };

    // 마우스가 외부로 벗어났을 때 이벤트
    const handleLegendLeave = () => {
        setSelectedIndex(null);
        setTooltipData((prev) => ({ ...prev }));
        setImageVisible(false);
    };

    /* mobile */
    // 터치했을 때 이벤트트
    const handleLegendClick = (index, event) => {
        if (!isImageData || !salesData || index < 0 || index >= salesData.length) return;

        event.stopPropagation(); // 내부 클릭 시 이벤트 전파 방지

        const clickedItem = salesData[index];

        if (selectedIndex === index) {
            setSelectedIndex(null);
            setSelectedImage((prev) => { return prev });
            setImageVisible(false);
        } else {
            setSelectedIndex(index);
            setSelectedImage(clickedItem.image);
            setImageVisible(true);
        }
    };

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false, //부모의 크기에 맞춤
        plugins: {
            legend: {
                display: !isImageData,
                position: "bottom",
                labels: {
                    font: {
                        size: 12,
                    },
                    boxWidth: 6,
                    boxHeight: 6,
                },
                onClick: ((isImageData && isMobile) ? handleLegendClick : undefined),
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const dataIndex = context.dataIndex;
                        const salesItem = salesData[dataIndex];
                        return `${label}:${unitTitle} ${salesItem.amount.toLocaleString()}${title}(${salesItem.ratio}%)`;
                    }
                }
            }
        },
        animation: {
            animateScale: true, // 크기 확대 애니메이션
            animateRotate: true // 회전 애니메이션
        }
    }), [salesData, isImageData]);

    const data = useMemo(() => {
        if (everyData) {
            return {
                labels: salesData.map(data => data.label),
                datasets: [
                    {
                        label: t("saleHistPage.sale"),
                        data: salesData.map(() => 1),
                        backgroundColor: salesData.map(() => "#B0B0B0"),
                        borderWidth: 0,
                    },
                ],
            };
        } else {
            salesData.sort((a,b) => b.amount - a.amount); // 차트 amount 값으로 내림차순 정렬
            return {
                labels: salesData.map(data => data.label),
                datasets: [
                    {
                        label: t("saleHistPage.sale"),
                        data: salesData.map(data => data.amount),
                        backgroundColor: salesData.map(data => data.backgroundColor),
                        borderWidth: 0,
                    },
                ],
            };
        }
    }, [salesData, everyData, t]);

    return (
        <ChartBox>
            <ChartImageWrap>
                <ChartContainer $hasImage={isImageData} $isMobile={isMobile}>
                    <Doughnut options={options} data={data} />
                </ChartContainer>
            </ChartImageWrap>
            {isImageData && (
                <LegendBox ref={legendRef}>
                    {salesData.map((item, index) => (
                        <LegendItem
                            key={index}
                            className="legend-item"
                            color={everyData ? "#B0B0B0" : item.backgroundColor}
                            onMouseEnter={!isMobile ? (event) => handleLegendHover(index, event) : undefined}
                            onMouseLeave={!isMobile ? handleLegendLeave : undefined}
                            onClick={!isMobile ? undefined : (event) => handleLegendClick(index, event)}
                            $isActive={selectedIndex === index}
                            $isMobile={isMobile}
                        >
                            <TooltipBox $imageVisible={imageVisible && selectedIndex === index} $isMobile={isMobile} $singleImage={isMobile ? selectedImage?.length === 1 : tooltipData.image?.length === 1}>
                                {!isMobile
                                    ? tooltipData.image?.map((img, idx) => (
                                        <StyledImage
                                            key={idx}
                                            src={img}
                                            alt={`Tooltip Image ${idx + 1}`}
                                            $imageVisible={imageVisible}
                                        />
                                    ))
                                    : selectedImage?.map((img, idx) => (
                                        <StyledImage
                                            key={idx}
                                            src={img}
                                            alt={`Tooltip Image ${idx + 1}`}
                                            $imageVisible={imageVisible}
                                        />
                                    ))
                                }
                            </TooltipBox>
                            <span></span>
                            <p>{item.label}</p>
                        </LegendItem>
                    ))}
                </LegendBox>
            )}
        </ChartBox>
    );
});

export default EqmtByTypeChart;